/**
 * AccessDenied.tsx
 *
 * Reusable "permission denied" UI component.
 *
 * Rules:
 * - MUST NOT expose tenantId / orgId.
 * - MUST NOT reveal which specific permission is missing (avoid enumeration).
 * - Provides a back-navigation affordance so the user is not stranded.
 */
import { useLocation } from 'wouter';
import { ShieldX } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export interface AccessDeniedProps {
  /** Human-readable name of the resource/page being denied. */
  resource?: string;
}

export default function AccessDenied({ resource }: AccessDeniedProps) {
  const [, navigate] = useLocation();

  return (
    <div
      className="flex min-h-[60vh] items-center justify-center"
      role="alert"
      aria-live="assertive"
    >
      <Card className="w-full max-w-md text-center">
        <CardHeader className="pb-2">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <ShieldX className="h-6 w-6 text-destructive" aria-hidden />
          </div>
          <CardTitle className="text-lg">权限不足</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {resource
              ? `您没有访问「${resource}」的权限。`
              : '您没有访问此页面的权限。'}
            如需开通权限，请联系系统管理员。
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/')}
          >
            返回首页
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
